/**
 * Design tokens for the program entry form redesign.
 *
 * Ported from `Program Entry Updates/tailwind.config.js`. The prototype was built in Tailwind.
 * This app does now have Tailwind, but scoped by `content` allowlist to the staff application
 * components and prefixed `tw-` (see `tailwind.config.js`); this form is outside it, so the
 * palette lives here and components consume it through inline style objects — the same
 * approach as `templates/HeaderNew.js`.
 *
 * Gray and amber are Tailwind's own defaults, inlined because the prototype's markup
 * leans on `gray-200`, `amber-100` and friends throughout.
 */

// Design brand ramp. brand[600] (#8E1B1B) is the prototype's primary; it sits a shade
// cooler than the site-wide #842014 used by HeaderNew.js.
export const brand = {
    50: '#FCF4F3',
    100: '#F7E4E2',
    200: '#EFC7C4',
    300: '#E09B97',
    400: '#CB6862',
    500: '#B03F39',
    600: '#8E1B1B',
    700: '#761616',
    800: '#5C1111',
    900: '#420C0C',
};

export const ink = {
    400: '#8A8F98',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2430',
    900: '#12151C',
};

export const gray = {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
};

// Warnings — the non-blocking half of validation.js.
export const amber = {
    50: '#FFFBEB',
    100: '#FEF3C7',
    200: '#FDE68A',
    700: '#B45309',
    800: '#92400E',
};

export const green = {
    100: '#DCFCE7',
    600: '#16A34A',
    700: '#15803D',
};

export const white = '#FFFFFF';

export const shadow = {
    card: '0 1px 2px rgba(18, 21, 28, 0.04), 0 8px 24px -12px rgba(18, 21, 28, 0.12)',
    tooltip: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
    header: '0 1px 2px rgba(18, 21, 28, 0.06)',
};

export const radius = {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    pill: '999px',
};

// The prototype's typeface is Inter, but the app never loads it and shipping a webfont for
// one modal was not worth the weight. Naming it here only produced a stack whose first entry
// always missed, so it is left out: this is the system sans the form actually renders in.
export const fontStack =
    "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

/**
 * Shared style objects. Anything used by more than one component lives here so the
 * sections stay mostly markup.
 */
export const s = {
    // ---- Typography -----------------------------------------------------------
    label: {
        fontSize: '13px',
        fontWeight: 600,
        lineHeight: 1,
        color: ink[800],
        marginBottom: 0,
    },
    hint: {
        marginTop: '4px',
        marginBottom: 0,
        fontSize: '11.5px',
        color: ink[400],
    },
    errorText: {
        marginTop: '4px',
        marginBottom: 0,
        fontSize: '11.5px',
        fontWeight: 500,
        color: brand[600],
        display: 'flex',
        alignItems: 'flex-start',
        gap: '4px',
    },
    warningText: {
        marginTop: '4px',
        marginBottom: 0,
        fontSize: '11.5px',
        fontWeight: 500,
        color: amber[700],
        display: 'flex',
        alignItems: 'flex-start',
        gap: '4px',
    },
    sectionTitle: {
        display: 'block',
        fontSize: '15px',
        fontWeight: 700,
        letterSpacing: '-0.01em',
        color: ink[900],
    },
    sectionBlurb: {
        display: 'block',
        fontSize: '12px',
        color: ink[500],
    },
    groupTitle: {
        fontSize: '12px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: ink[600],
        margin: 0,
    },

    // Controls themselves are not styled here. They wear `.pf-control` from
    // `programForm.css`, which is the one place the field box is described — inline styles
    // cannot express the `:focus`, `::placeholder` and `:disabled` states it needs, nor
    // reach into the antd and Quill markup that has to match it. See `ui/Inputs.js`.

    // ---- Cards ----------------------------------------------------------------
    card: {
        overflow: 'hidden',
        borderRadius: radius.xl,
        border: `1px solid ${gray[200]}`,
        backgroundColor: white,
        boxShadow: shadow.card,
    },
    cardHeader: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        width: '100%',
        padding: '16px 20px',
        textAlign: 'left',
        background: 'none',
        border: 'none',
        transition: 'background-color 0.15s',
    },
    cardBody: {
        padding: '20px',
    },
    subGroup: {
        borderRadius: radius.lg,
        border: `1px solid ${gray[200]}`,
        backgroundColor: gray[50],
        padding: '16px',
    },

    // ---- Badges ---------------------------------------------------------------
    stepBadge: {
        display: 'flex',
        height: '28px',
        width: '28px',
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: radius.pill,
        fontSize: '12px',
        fontWeight: 700,
        backgroundColor: brand[50],
        color: brand[700],
    },
    stepBadgeComplete: {
        backgroundColor: brand[600],
        color: white,
    },
    countPill: {
        borderRadius: radius.pill,
        backgroundColor: gray[100],
        padding: '4px 10px',
        fontSize: '11px',
        fontWeight: 600,
        color: ink[500],
        whiteSpace: 'nowrap',
    },
    warningPill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        borderRadius: radius.pill,
        backgroundColor: amber[100],
        padding: '4px 10px',
        fontSize: '11px',
        fontWeight: 700,
        color: amber[800],
        whiteSpace: 'nowrap',
    },

    // ---- Buttons --------------------------------------------------------------
    buttonPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        height: '38px',
        padding: '0 18px',
        fontSize: '13.5px',
        fontWeight: 600,
        color: white,
        backgroundColor: brand[600],
        border: `1px solid ${brand[600]}`,
        borderRadius: radius.md,
        cursor: 'pointer',
        transition: 'background-color 0.15s',
    },
    buttonSecondary: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        height: '38px',
        padding: '0 18px',
        fontSize: '13.5px',
        fontWeight: 600,
        color: ink[700],
        backgroundColor: white,
        border: `1px solid ${gray[300]}`,
        borderRadius: radius.md,
        cursor: 'pointer',
        transition: 'background-color 0.15s',
    },
    buttonGhost: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        fontSize: '12px',
        fontWeight: 600,
        color: brand[600],
        background: 'none',
        border: 'none',
        borderRadius: radius.sm,
        cursor: 'pointer',
    },
    buttonDisabled: {
        opacity: 0.55,
        cursor: 'not-allowed',
    },

    // ---- Screen-reader only ---------------------------------------------------
    srOnly: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0,0,0,0)',
        whiteSpace: 'nowrap',
        border: 0,
    },
};

// Hover states are set by mutating the node in an `onMouseEnter` handler, following
// `HeaderNew.js:709` — there is no CSS-in-JS here and no stylesheet to hang `:hover` off.
